// Added by Sejune Oh on 2020-04-06
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState, useEffect } from 'react'
import { createStyles, Theme, makeStyles, useTheme } from '@material-ui/core'
import cn from 'classnames'
import { v4 as uuidv4 } from 'uuid'

// ! Helper
import Intl from '@/helper/Intl'

// ! UI Components
import {
  Table as MaterialTable,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TableFooter,
} from '@/components/_primes/v2'

import Button from '@/components/_primes/Button'

// ! Sub components
import TablePaginationActions from './TablePaginationActions'
import EnhancedTableHead, { Order } from './EnhancedTableHead'

// ! Constants
import { ROWS_PER_PAGE } from './constants'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Interface
export interface Column {
  /** Column id (Unique) */
  id: string
  /** Column label (display name) */
  label: string
  /** Left, Right Padding 유무 */
  disablePadding?: boolean
  /** Sorting key로 사용할 수 있는지 여부 */
  sortable?: boolean
  /** Align */
  align?: 'left' | 'right' | 'center'
  /** Row에서 해당 Column에 매칭되는 값을 다르게 보여줄 때 */
  format?: (value: any) => string
  /** Button으로 된 Column인 경우 */
  getIsButton?: (value: any) => boolean
  /** Button Click Event */
  onClickBtn?: (e: React.MouseEvent<HTMLElement | HTMLButtonElement, MouseEvent>) => void
}

export interface TableEvtHandlerProps {
  /** Page 혹은 rowsPerPage 변경 evt handler */
  onTableChange?: ({ page, rowsPerPage }: { page: number; rowsPerPage: number }) => void
  /** Page sort evt handler */
  onTableSort?: ({ order, orderBy }: { order: Order; orderBy: string }) => void
  /** Row click evt handler */
  onRowClick?: (rowData: any) => void
}

export interface TableProps {
  /** Table rows */
  rows: any[]
  /** Table columns (Interface Column 참조) */
  columns: Column[]
  /** Rows per page */
  rowsPerPage?: number
  /** Current page (default: 0) */
  page?: number
  /** Order (default: '') */
  order?: Order
  /** OrderBy (default : desc) */
  orderBy?: string
  /** Total counts (All) */
  totalCount?: number
  /** Pagination 사용 유무 */
  hidePagination?: boolean
  /** Table body의 최대 Height */
  tableBodyMaxHeight?: string
  /** 각 Column의 사이즈 */
  colSizes?: string[]
  /** 각 Row의 height */
  rowHeight?: number
  /** 테이블 갱신용 property */
  searchedAt?: number
  /** Rows Per Page Options */
  rowsPerPageOptions?: number[]
  /** 추가로 노출해 줄 정보 */
  additionalInfo?: string
  labelRowsPerPage?: string
  className?: string
  disabled?: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Common Table UI 입니다.
 * @param rows
 * @param columns: Interface Column 참고.
 * @param rowsPerPage
 * @param page
 * @param order
 * @param orderBy
 * @param totalCount
 * @param hidePagination
 * @param colSizes: 각 Column의 사이즈
 * @param onTableChange: Page 혹은 rowsPerPage 변경 evt handler
 * @param onTableSort: Page sort evt handler
 * @param onRowClick: Row click evt handler
 * @param tableBodyMaxHeight: Table body의 최대 Height
 * @param labelRowsPerPage
 * @param additionalInfo: 추가로 노출해 줄 정보
 */
const Table: FC<TableProps & TableEvtHandlerProps> = ({
  rows,
  columns,
  rowsPerPage: rowsPerPageParam = ROWS_PER_PAGE[0],
  page: pageParam = 0,
  order: orderParam = 'desc',
  orderBy: orderByParam = '',
  onTableChange,
  onTableSort,
  totalCount,
  hidePagination = false,
  onRowClick,
  tableBodyMaxHeight,
  labelRowsPerPage: labelRowsPerPageParam,
  additionalInfo,
  colSizes,
  rowHeight,
  className,
  searchedAt,
  rowsPerPageOptions,
  disabled = false,
  ...props
}) => {
  const classes = useStyles(useTheme())

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! States
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageParam)
  const [page, setPage] = useState(pageParam)
  const [order, setOrder] = useState<Order>(orderParam)
  const [orderBy, setOrderBy] = useState<string>(orderByParam)

  const labelRowsPerPage =
    labelRowsPerPageParam || Intl.getIntlText({ id: 'htmljs_react.component.table.label_rows_per_page' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Functions
  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage)

    if (!onTableChange) return
    onTableChange({
      page: newPage,
      rowsPerPage,
    })
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rowsPerPage = +event.target.value

    setRowsPerPage(rowsPerPage)
    setPage(0)

    if (!onTableChange) return
    onTableChange({
      page: 0,
      rowsPerPage,
    })
  }
  const handleRequestSort = (event: React.MouseEvent<any>, property: string) => {
    const isDesc = orderBy === property && order === 'desc'
    setOrder(isDesc ? 'asc' : 'desc')
    setOrderBy(property)
  }

  const createClickHandler = (rowIndex: number) => (event: React.MouseEvent<any>) => {
    if (onRowClick) onRowClick(rows.find((rowData, index) => index === rowIndex))
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (onTableSort) {
      onTableSort({
        order,
        orderBy,
      })
    }

    // eslint-disable-next-line
  }, [order, orderBy])

  useEffect(() => {
    if (!!searchedAt) setPage(0)
  }, [searchedAt])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <TableContainer className={classes.container} style={{ maxHeight: tableBodyMaxHeight || 'auto' }}>
      <MaterialTable className={cn(className)} aria-label="sticky table">
        {colSizes && (
          <colgroup>
            {colSizes.map((colSize, colIndex) => (
              <col key={'col-i-' + colIndex} style={{ width: colSize }} />
            ))}
          </colgroup>
        )}
        {/* -------------- HEADER START -------------- */}
        <EnhancedTableHead headCells={columns} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
        {/* -------------- HEADER END -------------- */}

        {/* -------------- BODY START -------------- */}
        <TableBody>
          {rows.map((row, rowIndex) => {
            return (
              <TableRow style={{height: rowHeight}} hover tabIndex={-1} key={row.uid || uuidv4()} onClick={createClickHandler(rowIndex)}>
                {columns.map((column) => {
                  const value = column.format ? column.format(row) : row[column.id]
                  const isButton = column.getIsButton ? column.getIsButton(row) : false
                  return (
                    <TableCell
                      className={classes.tableCell}
                      data-disabled={disabled}
                      key={column.id}
                      align={column.align}
                      padding={column.disablePadding ? 'none' : 'normal'}
                      title={value}
                    >
                      {isButton ? (
                        <Button
                          btnText={column.id}
                          buttonType={column.id === 'cancel' || column.id === 'remove' ? 'danger' : 'task'}
                          className={classes.additionalBtn}
                          onClick={column.onClickBtn}
                          value={row.id}
                        />
                      ) : (
                        value
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
          {!hidePagination && rows.length !== rowsPerPage && (
            <TableRow style={{ height: (rowHeight || 53) * (rowsPerPage - rows.length) + 'px', cursor: 'default' }}>
              <TableCell colSpan={columns.length}></TableCell>
            </TableRow>
          )}
        </TableBody>
        {/* -------------- BODY END -------------- */}

        {/* -------------- PAGINATION START -------------- */}
        {!hidePagination && (
          <TableFooter className={classes.footer}>
            <TableRow className={classes.footerRow}>
              {additionalInfo && <TableCell>{additionalInfo}</TableCell>}
              <TablePagination
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions || ROWS_PER_PAGE}
                count={totalCount!}
                colSpan={columns.length - (additionalInfo ? 1 : 0)}
                page={page}
                labelRowsPerPage={labelRowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
        {/* -------------- PAGINATION END -------------- */}
      </MaterialTable>
    </TableContainer>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      '&::-webkit-scrollbar-thumb': {
        width: '50px',
        height: '50px',
        borderRadius: '4px',
        background: '#646464',
      },
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
    },
    footer: {
      position: 'relative',
    },
    footerRow: {
      '& > td': {
        borderBottom: 0,
      },
    },
    additionalBtn: {
      minWidth: '100%',
    },
    tableCell: {
      '&[data-disabled=true]': {
        color: 'rgba(255,255,255,0.3)'
      },
    }
  })
)

export default Table
